import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

function RuleEditor({ rule, onSaved }) {
  const [f, setF] = useState(rule);
  useEffect(() => { setF(rule); }, [rule]);
  const save = async () => {
    try { await api.put("/production/buffer", { type: f.type, value: Number(f.value) || 0 }); toast.success("Buffer rule saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Extra packs:</span>
      <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="h-9 rounded-md border border-input bg-white px-2" data-testid="buffer-type-select">
        <option value="percent">% of orders</option>
        <option value="fixed">fixed packs per product</option>
      </select>
      <Input type="number" min="0" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className="h-9 w-24" data-testid="buffer-value-input" />
      <Button size="sm" variant="outline" onClick={save} data-testid="buffer-save-btn"><Save className="mr-1 h-4 w-4" />Save rule</Button>
    </div>
  );
}

export function BufferPlan({ date, onApproved }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/production/buffer-plan", { params: { date } }).then((r) => setD(r.data)).catch(() => {}), [date]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const isAdmin = user?.role === "admin";
  const approve = async () => {
    try { await api.post("/production/plan/approve", { date }); toast.success("Production plan approved. Extra packs added to the kitchen plan and raw materials"); load(); onApproved?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-6 rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="buffer-plan">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-display text-lg font-semibold text-[#0F382C]">Planned packs with buffer</div>
        {d.approval
          ? <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700" data-testid="plan-approved"><ShieldCheck className="h-4 w-4" />Approved by {d.approval.approved_by}, {fmtDateTime(d.approval.approved_at)}</span>
          : <span className="text-sm font-semibold text-amber-700" data-testid="plan-not-approved">Waiting for owner approval</span>}
      </div>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="buffer-plan-note">{d.approval
        ? "The approved extra packs are already included in the kitchen plan and raw-material list above. New orders after approval are added on top."
        : "Extra packs are added to the kitchen plan and raw-material list only after the owner approves."}</p>
      {isAdmin ? <div className="mt-3"><RuleEditor rule={d.rule} onSaved={load} /></div>
        : <p className="mt-2 text-sm text-muted-foreground">Buffer rule: {d.rule.value}{d.rule.type === "percent" ? "% of orders" : " packs per product"}</p>}
      {d.rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="py-2">Product</th><th className="text-right">Ordered</th><th className="text-right">Buffer</th><th className="text-right">Make</th></tr></thead>
            <tbody>{d.rows.map((r) => (
              <tr key={r.product_id} className="border-t border-[#E7E2D6]" data-testid={`buffer-row-${r.product_id}`}>
                <td className="py-2 font-medium">{r.name}</td><td className="text-right font-mono">{r.ordered}</td>
                <td className="text-right font-mono text-amber-700">+{r.buffer}</td><td className="text-right font-mono font-semibold">{r.planned}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {isAdmin && d.rows.length > 0 && (
        <Button onClick={approve} className="mt-4 rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="approve-plan-btn">
          <ShieldCheck className="mr-1 h-4 w-4" />{d.approval ? "Approve again with current orders" : "Approve production plan"}
        </Button>
      )}
    </div>
  );
}
